<template>
  <div class="segmented-tabs">
    <div class="tabs-list" ref="tabsListRef">
      <div class="active-indicator" :style="activeIndicatorStyle"></div>
      <div
        v-for="item in items"
        :key="item.key"
        class="tab-item"
        :class="{
          active: modelValue === item.key,
          disabled: item.disabled,
        }"
        @click="handleClick(item)"
        ref="tabItems"
      >
        <div class="tab-item-content">
          <Icon :name="item.icon" v-if="item.icon" class="icon"></Icon>
          {{ item.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import Icon from './Icon.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'change'])
const tabsListRef = ref(null)
const tabItems = ref([])
const activeIndicatorStyle = ref({ opacity: 0 })

const updateActiveIndicator = () => {
  nextTick(() => {
    const tabs = Array.isArray(tabItems.value) ? tabItems.value : [tabItems.value].filter(Boolean)
    const activeIndex = props.items.findIndex((item) => item.key === props.modelValue)
    if (activeIndex < 0 || !tabs[activeIndex]) {
      activeIndicatorStyle.value = { opacity: 0 }
      return
    }

    const activeTab = tabs[activeIndex]
    activeIndicatorStyle.value = {
      left: `${activeTab.offsetLeft}px`,
      top: `${activeTab.offsetTop}px`,
      width: `${activeTab.offsetWidth}px`,
      height: `${activeTab.offsetHeight}px`,
      opacity: 1,
      transition: 'left 0.3s ease, width 0.3s ease, top 0.3s ease, height 0.3s ease',
    }
  })
}

watch(() => props.modelValue, updateActiveIndicator)
watch(() => props.items, updateActiveIndicator, { deep: true })

onMounted(() => {
  updateActiveIndicator()
  window.addEventListener('resize', updateActiveIndicator)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateActiveIndicator)
})

const handleClick = (item) => {
  if (item.disabled || props.disabled) return
  emit('update:modelValue', item.key)
  emit('change', item.key)
}
</script>

<style scoped lang="less">
.segmented-tabs {
  margin: 16px 0;

  .tabs-list {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    width: fit-content;
    max-width: 100%;
    padding: 4px;
    border-radius: 8px;
    background: #eae8f0;
    overflow: hidden;
    box-sizing: border-box;

    .active-indicator {
      position: absolute;
      border-radius: 6px;
      background: #fff;
      box-shadow: 0 1px 0 0 #0f06290d;
      z-index: 1;
      pointer-events: none;
      box-sizing: border-box;
    }

    .tab-item {
      padding: 6px 16px;
      color: var(--dark-2);
      cursor: pointer;
      transition: color 0.2s ease;
      background: transparent;
      border: none;
      position: relative;
      font-weight: 400;
      user-select: none;
      font-family: 'Sora', sans-serif;
      font-size: 15px;
      line-height: 24px;
      z-index: 2;
      box-sizing: border-box;
      white-space: nowrap;

      .tab-item-content {
        display: flex;
        align-items: center;
        gap: 8px;

        .icon {
          flex-shrink: 0;
        }
      }

      &:hover:not(.disabled):not(.active) {
        color: var(--primary-hover);
      }

      &.active:not(.disabled) {
        font-weight: 500;
        color: #0f0629;
      }

      &.disabled {
        cursor: not-allowed;
        color: var(--dark-4);
        opacity: 0.6;
      }
    }
  }

  .content {
    margin-top: 16px;
  }
}
</style>
